import React, {useCallback, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {FmlxModal, FmlxCheckBox} from 'fmlx-common-ui';
import withIntl from '../../lib/withIntl';
import {ExplorerContentTypes} from '../../constants/dataEnum';
import FormattedHTMLMessage from '../../components/FormattedHTMLMessage';

const ItemType = {
  PROTOCOL: 'PROTOCOL',
  PROTOCOLS: 'PROTOCOLS',
  FOLDER: 'FOLDER',
  FOLDERS: 'FOLDERS',
  PROTOCOL_AND_FOLDER: 'PROTOCOL_AND_FOLDER',
};

function getProtocolItemType(protocols = []) {
  const types = new Set();
  protocols.forEach((p) => types.add(p.type));

  if (types.size > 1) {
    return ItemType.PROTOCOL_AND_FOLDER;
  }

  const [type] = types;

  if (protocols.length > 1) {
    return type === ExplorerContentTypes.FOLDER ? ItemType.FOLDERS : ItemType.PROTOCOLS;
  }
  return type === ExplorerContentTypes.FOLDER ? ItemType.FOLDER : ItemType.PROTOCOL;
}

// could return protocol or folder
function getRunningItem(protocols, runningProtocol) {
  if (!runningProtocol) {
    return null;
  }
  return protocols.find((p) => {
    if (p.type === ExplorerContentTypes.FOLDER && runningProtocol.folderId) {
      const runningFolderIds = runningProtocol?.protocolFolderLineages?.map((e) => e.id) || Array(0);

      return runningFolderIds.includes(p.ID);
    }
    return p.ID === runningProtocol.id;
  });
}

function RemoveProtocolModal({intl, open, protocols, runningProtocol, onRemoveClick, onCancelClick, onGotItClick}) {
  const protocolItemType = useMemo(() => getProtocolItemType(protocols), [protocols]);
  const currentRunningItem = getRunningItem(protocols, runningProtocol);
  const isRunningProtocolExist = Boolean(currentRunningItem);

  const [isChecked, setIsChecked] = useState(false);

  const toggleCheckbox = useCallback(() => {
    setIsChecked(!isChecked);
  }, [isChecked]);

  const getModalTitle = () => {
    switch (protocolItemType) {
      // eslint-disable-next-line default-case-last
      default:
      case ItemType.PROTOCOL:
      case ItemType.PROTOCOLS:
        return intl.formatMessage({id: 'explorer.removeProtocolModal.title.protocols'});
      case ItemType.FOLDER:
      case ItemType.FOLDERS:
        return intl.formatMessage({id: 'explorer.removeProtocolModal.title.folders'});
      case ItemType.PROTOCOL_AND_FOLDER:
        return intl.formatMessage({id: 'explorer.removeProtocolModal.title.multipleItems'});
    }
  };

  const getContent = () => {
    let message = '';
    let checkBoxMessage = '';

    switch (protocolItemType) {
      // eslint-disable-next-line default-case-last
      default:
      case ItemType.PROTOCOL:
        message = (
          <FormattedHTMLMessage id="explorer.removeProtocolModal.message.protocol" values={{name: protocols[0].name}} />
        );
        checkBoxMessage = intl.formatMessage({id: 'explorer.removeProtocolModal.checkBoxMessage.protocols'});
        break;
      case ItemType.PROTOCOLS:
        message = <FormattedHTMLMessage id="explorer.removeProtocolModal.message.protocols" />;
        checkBoxMessage = intl.formatMessage({id: 'explorer.removeProtocolModal.checkBoxMessage.protocols'});
        break;
      case ItemType.FOLDER:
        message = (
          <FormattedHTMLMessage id="explorer.removeProtocolModal.message.folder" values={{name: protocols[0].name}} />
        );
        checkBoxMessage = intl.formatMessage({id: 'explorer.removeProtocolModal.checkBoxMessage.folders'});
        break;
      case ItemType.FOLDERS:
        message = <FormattedHTMLMessage id="explorer.removeProtocolModal.message.folders" />;
        checkBoxMessage = intl.formatMessage({id: 'explorer.removeProtocolModal.checkBoxMessage.folders'});
        break;
      case ItemType.PROTOCOL_AND_FOLDER:
        message = <FormattedHTMLMessage id="explorer.removeProtocolModal.message.multipleItems" />;
        checkBoxMessage = intl.formatMessage({id: 'explorer.removeProtocolModal.checkBoxMessage.multipleItems'});
        break;
    }

    return (
      <>
        <div className="message" style={{paddingBottom: 10}}>
          {message}
        </div>
        <FmlxCheckBox label={checkBoxMessage} size="sm" checked={isChecked} onChange={toggleCheckbox} />
      </>
    );
  };

  const getContentWithRunningProtocol = () => {
    if (!currentRunningItem) return null;

    const isRunningProtocolInsideFolder = currentRunningItem.type === ExplorerContentTypes.FOLDER;
    let message = '';

    if (isRunningProtocolInsideFolder) {
      const folderName = currentRunningItem.name;
      const protocolName = runningProtocol.name;
      message = (
        <FormattedHTMLMessage
          id="explorer.removeProtocolModal.message.runningProtocolFolder"
          values={{folderName, protocolName}}
        />
      );
    } else {
      const protocolName = currentRunningItem.name;
      message = (
        <FormattedHTMLMessage id="explorer.removeProtocolModal.message.runningProtocol" values={{protocolName}} />
      );
    }
    return (
      <div className="message" style={{paddingBottom: 10}}>
        {message}
      </div>
    );
  };

  const isDisableRemoveButton = () => !isChecked;

  return (
    <FmlxModal
      open={open}
      size="lg"
      title={getModalTitle()}
      hideCloseButton
      tertiaryButton={{show: false}}
      primaryButton={
        isRunningProtocolExist
          ? {
              label: intl.formatMessage({id: 'explorer.removeProtocolModal.button.gotIt'}),
              show: true,
              onClick: onGotItClick,
            }
          : {
              label: intl.formatMessage({id: 'explorer.removeProtocolModal.button.remove'}),
              disabled: isDisableRemoveButton(),
              show: true,
              type: 'danger',
              variant: 'contain',
              onClick: onRemoveClick,
            }
      }
      secondaryButton={
        isRunningProtocolExist
          ? {show: false}
          : {
              label: intl.formatMessage({id: 'explorer.removeProtocolModal.button.cancel'}),
              disabled: false,
              show: true,
              type: 'primary',
              variant: 'outline',
              onClick: onCancelClick,
            }
      }
    >
      <div className="remove-protocol-dialog">
        {isRunningProtocolExist ? getContentWithRunningProtocol() : getContent()}
      </div>
    </FmlxModal>
  );
}

RemoveProtocolModal.propTypes = {
  intl: PropTypes.any,
  open: PropTypes.bool,
  onRemoveClick: PropTypes.func,
  onCancelClick: PropTypes.func,
  onGotItClick: PropTypes.func,
  protocols: PropTypes.array.isRequired,
  runningProtocol: PropTypes.any,
};

RemoveProtocolModal.defaultProps = {
  intl: {},
  open: true,
  onRemoveClick: () => {},
  onCancelClick: () => {},
  onGotItClick: () => {},
  runningProtocol: undefined,
};

export default withIntl(RemoveProtocolModal);
